import React, { useReducer } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, ModalFooter, Alert, Button } from "reactstrap";
import SpinnerLabel from "common/components/spinner-label";
import { ADMIN_PAYMENT_METHOD } from "common/components/admin-payment-form-proptypes";
import StripeElementsProvider from "common/stripe/stripe-elements-provider";
import UpgradeSellSection from "./upgrade-sell-section";
import OrderSummarySection from "./order-summary-section";
import PaymentInformationSection from "./payment-information-section";
import AddCouponSection from "./add-coupon-section";
import StripeModalSubmitButton from "./stripe-modal-submit-button";
import computeTotalAmount from "./compute-total-amount";
import "./purchase-modal.styles.scss";

const propTypes = {
  display: PropTypes.bool.isRequired,
  toggleDisplay: PropTypes.func.isRequired,
  purchaseModalData: PropTypes.shape({
    buttonColor: PropTypes.string,
    confirmationButtonText: PropTypes.string,
    formTarget: PropTypes.string,
    applyCouponLink: PropTypes.string,
    hideCouponLine: PropTypes.bool,
    hideExistingCards: PropTypes.bool,
    overridePackage: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      recurring: PropTypes.number
    }),
    impressionBoost: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      impression_quantity: PropTypes.number,
      cost: PropTypes.number
    }),
    showOrderSummary: PropTypes.bool,
    showPaymentFailNote: PropTypes.bool,
    showUpgradeSell: PropTypes.bool
  }).isRequired,
  images: PropTypes.shape({
    cards: PropTypes.string,
    cards2x: PropTypes.string
  }).isRequired,
  campaignGroup: PropTypes.shape({
    attached_coupons: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        discount_percent: PropTypes.number,
        discount_cents: PropTypes.number
      })
    ),
    billable_credit_cards: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    ),
    campaign_package: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      recurring: PropTypes.number
    }),
    country_iso: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    site_credit_available: PropTypes.number,
    payment_method: PropTypes.string
  }),
  errorData: PropTypes.shape({
    base: PropTypes.node,
    billing_zip: PropTypes.node,
    credit_card_cvv: PropTypes.node,
    credit_card_expiration_month: PropTypes.node,
    credit_card_expiration_year: PropTypes.node,
    credit_card_number: PropTypes.node
  }),
  submitCallback: PropTypes.func,
  isProcessing: PropTypes.bool,
  showClientAdminPaymentWarning: PropTypes.bool.isRequired,
  showInvoicePurchasing: PropTypes.bool.isRequired,
  hideCreditCardPurchasing: PropTypes.bool.isRequired,
  accountholderName: PropTypes.string.isRequired,
  adminPaymentMethod: ADMIN_PAYMENT_METHOD,
  applyCouponCodeCallback: PropTypes.func.isRequired,
  isVerifyingCoupon: PropTypes.bool,
  couponData: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    status: PropTypes.string,
    partner_id: PropTypes.number,
    discount_cents: PropTypes.number,
    discount_percent: PropTypes.number,
    allow_other_coupons: PropTypes.bool
  }),
  couponErrorData: PropTypes.shape({
    code: PropTypes.arrayOf(PropTypes.string)
  }),
  stripeRetail: PropTypes.shape({
    enabled: PropTypes.bool,
    publishableKey: PropTypes.string,
    paymentMethodsConfigId: PropTypes.string,
    customerSessionPath: PropTypes.string,
    postPurchase: PropTypes.func,
    onComplete: PropTypes.func,
    onError: PropTypes.func
  })
};

const DEFAULT_STRIPE_RETAIL = {};

const reducer = (state, action) => {
  switch (action.type) {
    case "UPDATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const PurchaseModal = ({
  display,
  toggleDisplay,
  purchaseModalData,
  images,
  campaignGroup = {},
  errorData = {},
  submitCallback = null,
  isProcessing = false,
  showClientAdminPaymentWarning,
  showInvoicePurchasing,
  hideCreditCardPurchasing,
  accountholderName,
  applyCouponCodeCallback,
  isVerifyingCoupon = false,
  couponData = {},
  couponErrorData = {},
  adminPaymentMethod = null,
  stripeRetail = DEFAULT_STRIPE_RETAIL
}) => {
  const {
    enabled: stripeRetailEnabled = false,
    publishableKey: stripePublishableKey = null,
    paymentMethodsConfigId: stripePaymentMethodsConfigId = null,
    customerSessionPath = null,
    postPurchase: stripePostPurchase = null,
    onComplete: onStripeComplete = null,
    onError: onStripeError = null
  } = stripeRetail;

  const defaultCreditCard = () => {
    const cards = campaignGroup?.billable_credit_cards;
    return cards?.length > 0 ? cards[0].id : -1;
  };

  const defaultPaymentOption =
    showInvoicePurchasing && hideCreditCardPurchasing
      ? "stripe_invoice"
      : campaignGroup.payment_method;

  // Note: some of these state properties are not used in this component, but are set
  // by the OrderSummarySection or PaymentInformationSection updateStateCallbacks and
  // are needed upstream in the handleSubmit callback.
  const [state, dispatch] = useReducer(reducer, {
    useSiteCredit: campaignGroup.site_credit_available > 0,
    creditCardNumber: null,
    expirationMonth: null,
    expirationYear: null,
    cvv: null,
    zipCode: null,
    transactionId: defaultCreditCard(),
    paymentOption: defaultPaymentOption,
    coupon: null,
    adminPayment: "internal",
    adminCustomAmount: "",
    adminBillingGroupId: "",
    adminRenewalOption: "renew_with_selected_funding",
    restrictCancellation: false
  });

  const createUpdateAction = newState => {
    dispatch({ type: "UPDATE", payload: newState });
  };

  const campaignPackage = () => {
    if (purchaseModalData.overridePackage) {
      return purchaseModalData.overridePackage;
    }
    return campaignGroup.campaign_package || {};
  };

  const campaignImpressionBoost = () => {
    if (purchaseModalData.impressionBoost) {
      return purchaseModalData.impressionBoost;
    }
    return {};
  };

  const handleSubmit = event => {
    submitCallback(
      event,
      state,
      campaignPackage(),
      campaignImpressionBoost(),
      purchaseModalData.formTarget
    );
  };

  const totalAmount = computeTotalAmount({
    campaignPackage: campaignPackage(),
    impressionBoost: campaignImpressionBoost(),
    coupons: campaignGroup.attached_coupons,
    verifiedCoupon: couponData,
    siteCreditAmount: campaignGroup.site_credit_available,
    useSiteCredit: state.useSiteCredit,
    hideCouponLine: purchaseModalData.hideCouponLine
  });

  const isRecurring = Boolean(campaignPackage().recurring);
  const hidePaymentInfo = totalAmount === 0 && !isRecurring;

  const stripeSubmit = stripeFields =>
    stripePostPurchase(
      state,
      campaignPackage(),
      campaignImpressionBoost(),
      purchaseModalData.formTarget,
      stripeFields
    );

  const renderForm = stripeReady => (
    <form onSubmit={event => event.preventDefault()}>
      <ModalBody className=" px-4 py-0">
        {purchaseModalData.showPaymentFailNote && (
          <Alert color="danger">
            <span className="font-weight-bold">NOTE:</span> Your campaigns in&nbsp; &quot;Payment
            Failed&quot; status will be re-charged within 24 hours using&nbsp; your updated billing
            info.
          </Alert>
        )}
        {errorData?.base && errorData?.base?.length > 0 && (
          <Alert color="danger">{errorData.base[0]}</Alert>
        )}
        {purchaseModalData.showUpgradeSell && (
          <UpgradeSellSection campaignPackage={campaignPackage()} campaignGroup={campaignGroup} />
        )}
        {purchaseModalData.showOrderSummary && (
          <OrderSummarySection
            siteCreditAmount={campaignGroup.site_credit_available}
            coupons={campaignGroup.attached_coupons}
            verifiedCoupon={couponData}
            campaignPackage={campaignPackage()}
            useSiteCredit={state.useSiteCredit}
            updateStateCallback={createUpdateAction}
            hideCouponLine={purchaseModalData.hideCouponLine}
            countryIso={campaignGroup.country_iso}
            impressionBoost={campaignImpressionBoost()}
          />
        )}
        {!purchaseModalData.hideCouponLine && (
          <AddCouponSection
            verifyCouponUrl={purchaseModalData.applyCouponLink}
            applyCouponCodeCallback={applyCouponCodeCallback}
            couponErrorData={couponErrorData}
            couponData={couponData}
            isVerifyingCoupon={isVerifyingCoupon}
          />
        )}
        {!hidePaymentInfo && (
          <PaymentInformationSection
            cardInfo={campaignGroup.billable_credit_cards}
            images={images}
            hideExistingCards={purchaseModalData.hideExistingCards}
            updateStateCallback={createUpdateAction}
            errorData={errorData}
            showClientAdminPaymentWarning={showClientAdminPaymentWarning}
            showInvoicePurchasing={showInvoicePurchasing}
            hideCreditCardPurchasing={hideCreditCardPurchasing}
            accountholderName={accountholderName}
            paymentOption={state.paymentOption}
            adminPaymentMethod={adminPaymentMethod}
            adminForm={{
              adminPayment: state.adminPayment,
              adminCustomAmount: state.adminCustomAmount,
              adminBillingGroupId: state.adminBillingGroupId,
              adminRenewalOption: state.adminRenewalOption,
              restrictCancellation: state.restrictCancellation
            }}
            stripeRetailEnabled={stripeRetailEnabled}
            stripeReady={stripeReady}
          />
        )}
      </ModalBody>
      <ModalFooter className="pt-2 px-4 d-flex flex-column align-items-start">
        <small className="pb-2">
          All purchases subject to&nbsp;
          <a href="/terms">Terms & Conditions</a>
        </small>
        {stripeRetailEnabled && stripeReady && !hidePaymentInfo ? (
          <StripeModalSubmitButton
            label={purchaseModalData.confirmationButtonText}
            buttonColor={purchaseModalData.buttonColor || "primary"}
            isProcessing={isProcessing}
            submit={stripeSubmit}
            onComplete={onStripeComplete}
            onError={onStripeError}
          />
        ) : (
          <Button
            type="button"
            onClick={event => handleSubmit(event)}
            disabled={isProcessing || (stripeRetailEnabled && !stripeReady && !hidePaymentInfo)}
            color={purchaseModalData.buttonColor || "primary"}
            size="lg"
            className="ml-0 btn-sharp"
          >
            <SpinnerLabel spin={isProcessing} label={purchaseModalData.confirmationButtonText} />
          </Button>
        )}
      </ModalFooter>
    </form>
  );

  return (
    <>
      {display && (
        <Modal
          isOpen={display}
          size="lg"
          toggle={toggleDisplay}
          fade={false}
          className="account-app purchase-modal"
        >
          <ModalHeader className="pb-0" toggle={toggleDisplay} />
          <StripeElementsProvider
            enabled={stripeRetailEnabled && !hidePaymentInfo}
            publishableKey={stripePublishableKey}
            amount={totalAmount}
            customerSessionPath={customerSessionPath}
            paymentMethodsConfigId={stripePaymentMethodsConfigId}
          >
            {renderForm}
          </StripeElementsProvider>
        </Modal>
      )}
    </>
  );
};

PurchaseModal.propTypes = propTypes;

export default PurchaseModal;
